// Chat with LAMP: the person's thread, with cards for everything waiting on them, and the composer
// always at the bottom. After an action, focus moves to LAMP's answer so the conversation carries on
// from the keyboard; the thread is a live log for screen readers. An Owner typing "pause" (or "pause LAMP's agents:
// why") pauses LAMP's agents, and "resume" resumes them. Quick-reply chips above the composer start an order,
// open Privacy, and switch between Hindi and English. Files go with a message (Received Files): attached by the
// picker, the phone's camera, drag and drop or paste, shown by name before sending, and refused with a plain reason
// before upload when LAMP doesn't take them.
import { useCallback, useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type FormEvent } from "react";
import {
  ApiError,
  type AgentsStatus,
  type Api,
  type Card,
  type CardAction,
  type CardForm,
  type ChatEntry,
  type Failure,
  type FileKind,
  type MasterDataCorrections,
  type PartBomGiven,
  type ReceivedFile,
  type TreeChange,
} from "../api.js";
import { plainAmounts } from "../i18n/format.js";
import { otherLanguage, useLanguage, useWords } from "../i18n/words.js";
import { agentsCommand } from "../kill-switch/KillSwitch.js";
import { FailureNotice, nameFailure } from "../preview/Failure.js";
import { privacyCommand } from "../privacy/Privacy.js";
import { ChangePreview, appliedNotice } from "../settings/ChangePreview.js";
import { PAGES } from "../shell/nav.js";
import { usePolling } from "../shell/poll.js";
import { ACCEPT, base64Of, checkFile, MAX_FILE_MEGABYTES } from "./attach.js";
import { CardView } from "./CardView.js";
import { MasterDataCardView } from "./MasterDataCard.js";
import { BomDraftCardView } from "./BomDraftCard.js";
import { ReceivedFileCard } from "./ReceivedFileCard.js";
import { pagesNamedIn } from "./page-links.js";
import { suggestionsIn } from "./suggestions.js";
import { UndoEmail, emailOf, type PendingEmail } from "./UndoEmail.js";

/** A file chosen to go with the next message. */
interface Pending {
  readonly key: string;
  readonly file: File;
  readonly mediaType: string;
}

const MAX_MESSAGE_LENGTH = 2000;

export function Chat({
  api,
  focusWorkItem,
  owner = false,
  onAgents,
  onPrivacy,
}: {
  api: Api;
  focusWorkItem?: string | undefined;
  /** Only an Owner confirms a Tree Change drafted in chat, or pauses and resumes LAMP's agents. */
  owner?: boolean;
  /** Told whether LAMP's agents are paused after a "pause" or "resume" command. */
  onAgents?: (agents: AgentsStatus) => void;
  /** Opens Settings → Privacy, for "privacy" or "withdraw consent". */
  onPrivacy?: () => void;
}) {
  const w = useWords();
  const { language, setLanguage } = useLanguage();
  const [entries, setEntries] = useState<readonly ChatEntry[] | undefined>(undefined);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Failure | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  /** Why a change card's Confirm was refused, by the change: said on the card itself (#784). */
  const [refusedChanges, setRefusedChanges] = useState<Readonly<Record<string, string>>>({});
  // Why a card's last press was refused, by the thread entry it is on: said on it (#1006).
  const [refusedCards, setRefusedCards] = useState<Readonly<Record<string, Failure>>>({});
  const [focusId, setFocusId] = useState<string | undefined>(undefined);
  const [files, setFiles] = useState<readonly Pending[]>([]);
  const [dragging, setDragging] = useState(false);
  const [emailing, setEmailing] = useState<PendingEmail | undefined>(undefined);
  const thread = useRef<HTMLOListElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  /** How many answers have been shown: a poll asked before the latest one is stale. */
  const answered = useRef(0);

  useEffect(() => {
    let live = true;
    api.thread().then(
      (loaded) => {
        if (!live) return;
        setEntries(loaded);
        const card = focusWorkItem ? loaded.findLast((e) => e.card?.workItem === focusWorkItem) : undefined;
        setFocusId(card?.id);
      },
      (error: unknown) => live && setProblem(nameFailure(error, w)),
    );
    return () => {
      live = false;
    };
  }, [api, focusWorkItem]);

  // What LAMP or a Run adds to the thread shows up without a reload (#512); not while the person is sending, and a
  // poll still on its way when a message is answered never puts back the thread from before it.
  usePolling(() => {
    if (busy) return;
    const asked = answered.current;
    api.thread().then(
      (loaded) => {
        if (answered.current !== asked) return;
        setEntries((shown) =>
          shown && loaded.length === shown.length && loaded.at(-1)?.id === shown.at(-1)?.id ? shown : loaded,
        );
      },
      () => undefined,
    );
  });

  // Moves focus to the entry asked for, or keeps the newest in view.
  useEffect(() => {
    if (!entries) return;
    const target = focusId ? thread.current?.querySelector<HTMLElement>(`[data-entry="${focusId}"]`) : null;
    if (target) {
      target.focus();
      target.scrollIntoView?.({ block: "nearest" });
    } else {
      thread.current?.lastElementChild?.scrollIntoView?.({ block: "end" });
    }
  }, [entries, focusId]);

  const answer = useCallback((next: readonly ChatEntry[]) => {
    answered.current += 1;
    setEntries(next);
    setFocusId(next.findLast((e) => e.author === "lamp")?.id);
  }, []);

  /** Adds files to the next message; one LAMP doesn't take is refused with why, before anything is uploaded. */
  const add = (chosen: Iterable<File>) => {
    const taken: Pending[] = [];
    for (const file of chosen) {
      const checked = checkFile(file);
      if (!checked.ok) {
        setProblem({
          problem:
            checked.reason === "type"
              ? w.chatFileType(file.name)
              : checked.reason === "size"
                ? w.chatFileSize(file.name, MAX_FILE_MEGABYTES)
                : w.chatFileEmpty(file.name),
          recovery: w.chatFileRecovery,
        });
        continue;
      }
      taken.push({
        key: `${file.name}:${file.size}:${file.lastModified}:${Math.random()}`,
        file,
        mediaType: checked.mediaType,
      });
    }
    if (taken.length > 0) setFiles((now) => [...now, ...taken]);
    input.current?.focus();
  };

  const drop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    add(event.dataTransfer.files);
  };

  const paste = (event: ClipboardEvent) => {
    if (event.clipboardData.files.length === 0) return;
    event.preventDefault();
    add(event.clipboardData.files);
  };

  /** Sends the chosen files, each its own Received File; the person's words go with the first. */
  const sendFiles = async (message: string) => {
    let words: string | undefined = message === "" ? undefined : message;
    let latest: readonly ChatEntry[] | undefined;
    for (const pending of files) {
      latest = await api.sendFile(
        { name: pending.file.name, mediaType: pending.mediaType, data: await base64Of(pending.file) },
        words,
      );
      words = undefined;
      setFiles((now) => now.filter((f) => f.key !== pending.key));
    }
    if (latest) answer(latest);
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const message = text.trim();
    if (busy) return;
    if (files.length > 0) {
      setBusy(true);
      setProblem(undefined);
      try {
        await sendFiles(message);
        setText("");
      } catch (error) {
        setProblem(nameFailure(error, w));
        input.current?.focus();
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!message) return;
    if (privacyCommand(message) && onPrivacy) {
      setText("");
      onPrivacy();
      return;
    }
    const command = agentsCommand(message, w);
    if (command && !owner) {
      setProblem({
        problem: w.chatOwnerOnly,
        recovery: w.chatAskOwner,
      });
      return;
    }
    setBusy(true);
    setProblem(undefined);
    setNotice(undefined);
    try {
      if (command) {
        const agents = command.kind === "pause" ? await api.pauseAgents(command.reason) : await api.resumeAgents();
        onAgents?.(agents);
        setNotice(agents.notice ?? w.chatWorking(agents.held));
      } else {
        answer(await api.send(message));
      }
      setText("");
    } catch (error) {
      setProblem(nameFailure(error, w));
      input.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  /** Says a sentence LAMP offered (#784), as if the person had typed it. */
  const say = async (sentence: string) => {
    setBusy(true);
    setProblem(undefined);
    try {
      answer(await api.send(sentence));
    } catch (error) {
      setProblem(nameFailure(error, w));
    } finally {
      setBusy(false);
    }
  };

  /** Takes up LAMP's offer to add what an order named and the business lacks, as the person's reply. */
  const addThem = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      answer(await api.send(w.chatAddThemText));
    } catch (error) {
      setProblem(nameFailure(error, w));
    } finally {
      setBusy(false);
    }
  };

  const clearEmailing = useCallback(() => setEmailing(undefined), []);
  /** A press on the card of thread entry `on`. */
  const act = async (
    on: string,
    card: Card,
    action: CardAction,
    input?: { reason?: string; text?: string; form?: CardForm },
  ) => {
    setBusy(true);
    setProblem(undefined);
    setRefusedCards((refused) => Object.fromEntries(Object.entries(refused).filter(([key]) => key !== on)));
    try {
      const reply = await api.act(card, action, input);
      answer(reply);
      // A card that emails a customer: the email waits its undo window on the server, unless the reply says none goes.
      const email = action === "confirm" ? emailOf(card, reply) : undefined;
      if (email) setEmailing(email);
    } catch (error) {
      // A press refused is said on the card pressed, which is left as it stands, never only below the thread (#1006).
      setRefusedCards((refused) => ({ ...refused, [on]: nameFailure(error, w) }));
      // The card moved on meanwhile (e.g. LAMP numbered it again): show the thread as it now stands.
      if (error instanceof ApiError && error.status === 409) await api.thread().then(answer, () => undefined);
    } finally {
      setBusy(false);
    }
  };

  /** An answer about a Received File: the kind, a retry or recording what LAMP read. */
  const onFile = async (call: () => Promise<readonly ChatEntry[]>) => {
    setBusy(true);
    setProblem(undefined);
    try {
      answer(await call());
    } catch (error) {
      setProblem(nameFailure(error, w));
      if (error instanceof ApiError && error.status === 409) await api.thread().then(answer, () => undefined);
    } finally {
      setBusy(false);
    }
  };
  /** Confirms a drafted customer, supplier or Item; a refusal is said in the thread too, so the thread is read again. */
  const confirmDraft = async (message: string, corrected?: MasterDataCorrections) => {
    setBusy(true);
    setProblem(undefined);
    try {
      answer(await api.confirmMasterData(message, corrected));
    } catch (error) {
      setProblem(nameFailure(error, w));
      await api.thread().then(answer, () => undefined);
      throw error;
    } finally {
      setBusy(false);
    }
  };
  /** Confirms a drafted BOM and routing; a refusal is said in the thread too, so the thread is read again (#565). */
  const confirmBom = async (message: string, given?: PartBomGiven) => {
    setBusy(true);
    setProblem(undefined);
    try {
      answer(await api.confirmBom(message, given));
    } catch (error) {
      setProblem(nameFailure(error, w));
      await api.thread().then(answer, () => undefined);
      throw error;
    } finally {
      setBusy(false);
    }
  };
  const chooseKind = (file: ReceivedFile, kind: "yes" | "none" | FileKind) =>
    void onFile(() => api.chooseFileKind(file.id, kind));

  const confirmChange = async (change: TreeChange, migrate: boolean) => {
    setBusy(true);
    setProblem(undefined);
    try {
      setNotice(appliedNotice(await api.confirmChange(change.id, migrate), w));
    } catch (error) {
      // A change the API refused, such as one drafted before another change, says why on its own card.
      if (error instanceof ApiError) {
        const { problem: why } = nameFailure(error, w);
        setRefusedChanges((refused) => ({ ...refused, [change.id]: why }));
      } else setProblem(nameFailure(error, w));
    } finally {
      // The card is read live: show it as it now stands.
      await api.thread().then(setEntries, () => undefined);
      setBusy(false);
    }
  };

  return (
    <div
      className={dragging ? "chat dragging" : "chat"}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) setDragging(false);
      }}
      onDrop={drop}
    >
      <ol ref={thread} className="thread" aria-label={w.chatThread} aria-live="polite" aria-busy={busy}>
        {entries === undefined ? <li className="muted">{w.chatLoading}</li> : null}
        {entries?.length === 0 ? <li className="muted">{w.chatEmpty}</li> : null}
        {entries?.map((entry) => (
          <li
            key={entry.id}
            className={entry.author === "person" ? "msg me" : "msg lamp"}
            data-entry={entry.id}
            tabIndex={-1}
          >
            <span className="by">{entry.author === "person" ? w.chatYou : w.chatLamp}</span>
            <p>{entry.author === "person" ? entry.text : plainAmounts(entry.text)}</p>
            {entry.file ? (
              <a className="file-chip" href={entry.file.href} download={entry.file.name}>
                <span aria-hidden="true">📎 </span>
                <span className="sr-only">{w.chatOpenFile(entry.file.name)}</span>
                <span aria-hidden="true">{entry.file.name}</span>
              </a>
            ) : null}
            {entry.receivedFile ? (
              <ReceivedFileCard
                file={entry.receivedFile}
                busy={busy}
                onChoose={chooseKind}
                onRetry={(file) => void onFile(() => api.retryFile(file.id))}
                onRecord={(file, corrected) => void onFile(() => api.recordFile(file.id, corrected))}
                onDevelop={(file) => void onFile(() => api.developFile(file.id))}
                onKeep={(file, keep) => void onFile(() => api.keepFile(file.id, keep))}
                onSendBack={(file, reason) => void onFile(() => api.sendBackFile(file.id, reason))}
              />
            ) : null}
            {entry.author === "lamp" && suggestionsIn(entry.text).length > 0 ? (
              <p className="chips">
                {suggestionsIn(entry.text).map((sentence) => (
                  <button
                    key={sentence}
                    type="button"
                    className="chip"
                    disabled={busy}
                    onClick={() => void say(sentence)}
                  >
                    {sentence}
                  </button>
                ))}
              </p>
            ) : null}
            {entry.author === "lamp"
              ? pagesNamedIn(entry.text).map((page) => (
                  <a key={page} className="btn" href={PAGES[page].path}>
                    {w.chatOpenPage(w[PAGES[page].title])}
                  </a>
                ))
              : null}
            {entry.offer === "add_them" ? (
              <button type="button" className="btn" disabled={busy} onClick={() => void addThem()}>
                {w.chatAddThem}
              </button>
            ) : null}
            {entry.masterData ? (
              <MasterDataCardView
                card={entry.masterData}
                busy={busy}
                onConfirm={(corrected) => confirmDraft(entry.masterData?.message ?? "", corrected)}
              />
            ) : null}
            {entry.bom ? (
              <BomDraftCardView
                card={entry.bom}
                busy={busy}
                onConfirm={(given) => confirmBom(entry.bom?.message ?? "", given)}
              />
            ) : null}
            {entry.card ? (
              <CardView
                card={entry.card}
                busy={busy}
                onAct={(card, action, input) => void act(entry.id, card, action, input)}
                refusal={refusedCards[entry.id]}
              />
            ) : null}
            {!entry.card && entry.run ? (
              <a className="btn" href={`/runs/${entry.run}`}>
                {w.todayOpenRun}
              </a>
            ) : null}
            {entry.treeChange ? (
              <ChangePreview
                change={entry.treeChange}
                owner={owner}
                busy={busy}
                onConfirm={(c, m) => void confirmChange(c, m)}
                refusal={refusedChanges[entry.treeChange.id]}
              />
            ) : null}
          </li>
        ))}
      </ol>
      <div className="chips" role="group" aria-label={w.chatChips}>
        <button
          type="button"
          className="chip"
          onClick={() => {
            setText(w.chatChipOrderText);
            input.current?.focus();
          }}
        >
          {w.chatChipOrder}
        </button>
        {onPrivacy ? (
          <button type="button" className="chip" onClick={onPrivacy}>
            {w.chatChipPrivacy}
          </button>
        ) : null}
        <button type="button" className="chip" onClick={() => setLanguage(otherLanguage(language))}>
          {w.languageSwitch}
        </button>
      </div>
      {files.length > 0 ? (
        <ul className="file-chips" aria-label={w.chatAttached}>
          {files.map((pending) => (
            <li key={pending.key} className="file-chip">
              <span aria-hidden="true">📎 </span>
              {pending.file.name}
              <button
                type="button"
                className="linkish"
                aria-label={w.chatRemoveFile(pending.file.name)}
                disabled={busy}
                onClick={() => {
                  setFiles((now) => now.filter((f) => f.key !== pending.key));
                  input.current?.focus();
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {dragging ? <p className="muted">{w.chatDropHint}</p> : null}
      {emailing ? (
        <UndoEmail
          key={emailing.workItem}
          pending={emailing}
          onUndo={async (pending) => {
            const undone = await api.undoEmail(pending);
            await api.thread().then(answer, () => undefined);
            return undone;
          }}
          onGone={clearEmailing}
        />
      ) : null}
      <form className="composer" onSubmit={send}>
        <input
          ref={picker}
          type="file"
          multiple
          accept={ACCEPT}
          hidden
          aria-hidden="true"
          tabIndex={-1}
          onChange={(event) => {
            if (event.target.files) add(event.target.files);
            event.target.value = "";
          }}
        />
        <input
          ref={camera}
          type="file"
          accept="image/jpeg,image/png"
          capture="environment"
          hidden
          aria-hidden="true"
          tabIndex={-1}
          onChange={(event) => {
            if (event.target.files) add(event.target.files);
            event.target.value = "";
          }}
        />
        <button
          type="button"
          className="btn attach"
          aria-label={w.chatAttach}
          title={w.chatAttach}
          disabled={busy}
          onClick={() => picker.current?.click()}
        >
          <span aria-hidden="true">📎</span>
        </button>
        <button
          type="button"
          className="btn attach camera"
          aria-label={w.chatCamera}
          title={w.chatCamera}
          disabled={busy}
          onClick={() => camera.current?.click()}
        >
          <span aria-hidden="true">📷</span>
        </button>
        <label htmlFor="composer-input" className="sr-only">
          {w.chatMessage}
        </label>
        <input
          id="composer-input"
          ref={input}
          value={text}
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder={w.chatPlaceholder}
          autoComplete="off"
          onChange={(event) => setText(event.target.value)}
          onPaste={paste}
          aria-describedby={problem ? "chat-problem" : undefined}
        />
        <button type="submit" className="btn btn-go" disabled={busy || (text.trim() === "" && files.length === 0)}>
          {busy ? w.chatSending : w.chatSend}
        </button>
      </form>
      {problem ? <FailureNotice id="chat-problem" failure={problem} /> : null}
      {notice ? <p role="status">{notice}</p> : null}
    </div>
  );
}
